require('./accountHeaderDetails.scss');

// @vendor
const React = require('react');
const PureRenderMixin = require('react-addons-pure-render-mixin');
const classNames = require('classnames');
const PropTypes = require('prop-types');

// @core
const { FormattedText, getLanguage } = require('core/i18n').i18n;

// @commons
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');

// @components
const { OneLineSymbolAmount } = require('commonsComponents/oneLineSymbolAmount');
const ContactCenterRender = require('components/conditionalRender/contactCenterRender')
const { AccountWarningMessage } = require('components/contactCenter/accountWarningMessage/accountWarningMessage');
import FeatureTogglingRender from 'components/conditionalRender/featureTogglingRender';

// @helpers
const { formatAmountObject, getPlainAmountObject } = require('utilities/currencyHelper');
const language = getLanguage();

// @ constants
const { CREDIT } = require('constants/flagBasedFeatures');

class AccountHeaderDetails extends React.Component {
    constructor(props) {
        super(props);
        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    buildOneLineSymbolAmount({ fieldName, modifier, className }) {
        const { immAccount } = this.props;
        const immAccountBalances = immAccount.get('balances');
        const plainAmountObject = getPlainAmountObject(immAccountBalances.get(fieldName));

        return (
            <OneLineSymbolAmount
                amountObject={plainAmountObject}
                modifier={modifier}
                className={className}
                language={language}
                amountObjectFormatted={formatAmountObject(plainAmountObject, true)}
            />
        );
    }

    buildCCExtraInfo() {
        if (!__CONTACT_CENTER__) return null;
        const totalAuthorizedOverdraft = (
            <div>
                {this.buildOneLineSymbolAmount({ fieldName: 'totalAuthorizedOverdraft', modifier: '+', className: 'account-header-details__tooltip-amount' })}
                <FormattedText
                    value="accounts-totalAuthorizedOverdraft"
                    className="account-header-details__tooltip-amount-description" />
            </div>
        );

        const totalPendingConsolidate = (
            <div>
                {this.buildOneLineSymbolAmount({ fieldName: 'totalPendingConsolidate', modifier: '-', className: 'account-header-details__tooltip-amount' })}
                <FormattedText
                    value="accounts-totalPendingConsolidate"
                    className="account-header-details__tooltip-amount-description" />
            </div>
        );

        const totalPendingAuthorize = (
            <div>
                {this.buildOneLineSymbolAmount({ fieldName: 'totalPendingAuthorize', modifier: '-', className: 'account-header-details__tooltip-amount' })}
                <FormattedText
                    value="accounts-totalPendingAuthorize"
                    className="account-header-details__tooltip-amount-description" />
            </div>
        );

        return {
            totalAuthorizedOverdraft,
            totalPendingConsolidate,
            totalPendingAuthorize
        };
    }

    buildTooltipContent() {
        const { immAccount } = this.props;
        const plainAmountObject = getPlainAmountObject(immAccount.get('balance'));
        const plainAmountBalanceObject = getPlainAmountObject(immAccount.get('balances').get('pregrantedCredit'));
        const plainAmountTotalObject = getPlainAmountObject(immAccount.get('balances').get('totalPendings'));
        const plainAmountAvailableObject = getPlainAmountObject(immAccount.get('balances').get('availableBalance'));
        const ccExtraInfo = __CONTACT_CENTER__ ? this.buildCCExtraInfo() : null;

        return (
            <article className="account-header-details__tooltip-content" id="sec-AccountHeaderDetails-Balance">
                <header className="account-header-details__tooltip-content-header">
                    <FormattedText value="accounts-howToCalculateAvailableBalance" />
                </header>
                <section className="account-header-details__tooltip-content-section">
                    <div>
                        <OneLineSymbolAmount
                            amountObject={plainAmountObject}
                            className="account-header-details__tooltip-amount  account-header-details__tooltip-amount--countable"
                            language={language}
                            amountObjectFormatted={formatAmountObject(plainAmountObject, true)}
                        />
                        <FormattedText value="accounts-balance" className="account-header-details__tooltip-amount-description" />
                    </div>
                    <FeatureTogglingRender notAllowedProductKey={CREDIT} >
                        <div>
                            <OneLineSymbolAmount
                                amountObject={plainAmountBalanceObject}
                                modifier={'+'}
                                className="account-header-details__tooltip-amount  account-header-details__tooltip-amount--credits"
                                language={language}
                                amountObjectFormatted={formatAmountObject(plainAmountBalanceObject, true)}
                            />
                            <FormattedText value="accounts-credits" className="account-header-details__tooltip-amount-description" />
                        </div>
                    </FeatureTogglingRender>
                    <ContactCenterRender>
                        {ccExtraInfo && ccExtraInfo.totalAuthorizedOverdraft}
                        {ccExtraInfo && ccExtraInfo.totalPendingConsolidate}
                        {ccExtraInfo && ccExtraInfo.totalPendingAuthorize}
                    </ContactCenterRender>
                    <div>
                        <OneLineSymbolAmount
                            amountObject={plainAmountTotalObject}
                            modifier={'-'}
                            className="account-header-details__tooltip-amount  account-header-details__tooltip-amount--pending"
                            language={language}
                            amountObjectFormatted={formatAmountObject(plainAmountTotalObject, true)}
                        />
                        <FormattedText value="accounts-pending" className="account-header-details__tooltip-amount-description" />
                    </div>
                </section>
                <footer className="account-header-details__tooltip-content-footer">
                    <div>
                        <OneLineSymbolAmount
                            amountObject={plainAmountAvailableObject}
                            className="account-header-details__tooltip-amount  account-header-details__tooltip-amount--available"
                            language={language}
                            amountObjectFormatted={formatAmountObject(plainAmountAvailableObject, true)}
                        />
                        <FormattedText value="accounts-availableBalance" className="account-header-details__tooltip-amount-description" />
                    </div>
                </footer>
            </article>
        )
    }

    render () {
        const { immAccount } = this.props;
        const tooltipTarget = <i className="icon icon-informacion"></i>
        const tooltipContent = this.buildTooltipContent();
        const availableBalance = immAccount.getIn(['balances', 'availableBalance']);
        const balance = immAccount.get('balance');
        const plainAmountObject = getPlainAmountObject(availableBalance);
        const plainAmountBalanceObject = getPlainAmountObject(balance);
        const isCanceledAccount = !!immAccount.getIn(['filters', 'canceled'])
        const warningMessageClass = classNames({ 'account-header-details__warning': isCanceledAccount });

        return (
            <div className="account-header-details">
                <div className="account-header-details__section">
                    <div className="account-header-details__available-balance hidden-xs">
                        <span className="account-header-details__title">
                            <OneLineSymbolAmount
                                amountObject={plainAmountObject}
                                className="account-header-details__amount"
                                language={language}
                                amountObjectFormatted={formatAmountObject(plainAmountObject, true)}
                            />
                        </span>
                        <span className="account-header-details__subtitle">
                            <OKTooltip
                                content={tooltipContent}
                                modifier="dark-bg"
                                offset={[-15, 0]}
                                placement="left"
                                target={tooltipTarget}
                                type="custom"
                            />
                            <FormattedText value="accounts-availableBalance" />
                        </span>
                    </div>
                    <div className="account-header-details__available-balance visible-xs">
                        <span className="account-header-details__subtitle">
                            <FormattedText value="accounts-availableBalance" />
                            <span className="account-header-details__title">
                                <OneLineSymbolAmount
                                    amountObject={plainAmountObject}
                                    className="account-header-details__amount"
                                    language={language}
                                    amountObjectFormatted={formatAmountObject(plainAmountObject, true)}
                                />
                            </span>
                            <OKTooltip
                                content={tooltipContent}
                                modifier="dark-bg"
                                offset={[-15, 0]}
                                placement="left"
                                target={tooltipTarget}
                                type="custom"
                            />
                        </span>
                    </div>
                </div>
                <div className="account-header-details__section hidden-xs hidden-sm">
                    <span className="account-header-details__title">
                        <OneLineSymbolAmount
                            amountObject={plainAmountBalanceObject}
                            className="account-header-details__amount"
                            language={language}
                            amountObjectFormatted={formatAmountObject(plainAmountBalanceObject, true)}
                        />
                    </span>
                    <span className="account-header-details__subtitle">
                        <FormattedText value="accounts-countableBalance" />
                    </span>
                </div>
                <ContactCenterRender>
                    <div className={warningMessageClass}>
                        <AccountWarningMessage immAccount={immAccount} />
                    </div>
                </ContactCenterRender>
            </div>
        );
    }
}

AccountHeaderDetails.propTypes = {
    immAccount: PropTypes.object.isRequired,
    postAlias: PropTypes.func
};

module.exports = AccountHeaderDetails;